'use client';

import { useQuery } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { getPrizeEntryApi } from '@flipster/apis/v1/campaign';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { Datetime } from '@flipster/react/design-utils/datetime';
import { css } from '@flipster/styled-system/css';
import { flex, vstack } from '@flipster/styled-system/patterns';
import { timestampToDate } from '@flipster/utils/datetime';
import { numberFormatter } from '@flipster/utils/num';

import { spacing6, spacing11 } from '@web/app/[locale]/campaign/[slug]/styles';

import PrizeEntryBoard from './prize-entry-board';

interface PrizeEntryProps {
  slug: string;
}

const PrizeEntry = ({ slug }: PrizeEntryProps) => {
  const { isCookieLoggedInAtom, isLoggedInAtom } = useBunja(authBunja);
  const isCookieLoggedIn = useAtomValue(isCookieLoggedInAtom);
  const isLoggedIn = useAtomValue(isLoggedInAtom);

  const { data } = useQuery({
    queryKey: ['getPrizeEntryApi', slug, isCookieLoggedIn],
    queryFn: () => getPrizeEntryApi({ eventId: slug }),
    select: (res) => res.data,
  });

  if (!data) return null;

  const availableTickets = Math.max(0, (data.myTicket?.total ?? 0) - (data.myTicket?.used ?? 0));
  const availableDrawEntries = !isLoggedIn
    ? '-'
    : data.drawStatus === 'UPCOMING'
      ? 'In progress'
      : numberFormatter(availableTickets);

  return (
    <section
      className={vstack({
        color: 'content.default.level-3',
        textAlign: 'center',
        py: { base: spacing11, tablet: 120 },
        maxW: 'desktopMaxWidth',
        mx: 'auto',
        boxSizing: 'content-box',
        px: spacing6,
      })}
      data-section-type='draw'
    >
      <div className={css({ w: { base: '100%', tablet: 590 }, maxW: 590, mb: 40 })}>
        <h2
          className={css({
            textStyle: 'Display/XL',
            color: 'content.default.level-4',
            textAlign: 'center',
          })}
        >
          Get your prizes now
        </h2>
        <div
          className={css({
            textStyle: 'Title/S',
            color: 'content.default.level-4',
            textAlign: 'center',
            mt: 12,
          })}
        >
          <Datetime value={timestampToDate(data.startsAt)} format='MMM dd, yyyy' isUTC />
          <span> - </span>
          <Datetime value={timestampToDate(data.endsAt)} format='MMM dd, yyyy' isUTC />
          <span>
            {' '}
            (<Datetime value={timestampToDate(data.endsAt)} format='HH:mm' isUTC withUTCText />)
          </span>
        </div>
        <div
          className={flex({
            w: '100%',
            p: 16,
            rounded: 8,
            bgColor: 'background.default.level-2',
            justify: 'space-between',
            align: 'center',
            mt: 24,
          })}
        >
          <p
            className={css({
              textStyle: 'Title/S',
              color: 'content.accent.promotion',
              whiteSpace: { tabletDown: 'pre-line' },
              textAlign: 'left',
            })}
          >
            {'Your available \ndraw entries'}
          </p>
          <p
            className={css({
              textStyle: 'Title/S',
              color: 'content.default.level-4',
              textAlign: 'right',
            })}
          >
            {availableDrawEntries}
          </p>
        </div>
        <p
          className={css({
            textStyle: 'Title/S',
            color: data.drawStatus === 'FINISHED' ? 'content.state.warning' : 'content.default.level-4',
            textAlign: 'center',
            mt: 24,
            whiteSpace: 'pre-line',
          })}
        >
          {data.drawStatus === 'FINISHED'
            ? 'The event has ended.\nPlease stay tuned for the winner announcement.'
            : 'The more entries you put in, the higher your chances of winning.\nWinners will be announced at the last day of event.'}
        </p>
      </div>
      <PrizeEntryBoard
        items={data.items}
        availableTickets={availableTickets}
        slug={slug}
        drawStatus={data.drawStatus}
      />
    </section>
  );
};

export default PrizeEntry;
